// @flow

import React, { type Node, useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';
import { useUserInputValue } from '@tectonics/prs/user-input-values';
import { useSubmitting } from '@tectonics/prs/submitting';
import { Button } from '../../../../atoms';
import spinnerImageSrc from './spinner.svg';
import padlockImageSrc from './padlock.svg';
import { useThemeId } from '@tectonics/prs/merchant';
import { getCountry, useIncomingPayment, getSafePaymentTypeCode } from '@tectonics/prs/incoming-payment';
import { useCurrentAllFeatures } from '@tectonics/prs/payment-spec';
import { hasFeature, type Feature } from '@tectonics/prs/feature';
import { GoogleButton } from '@tectonics/look-and-feel/volcanes/pages/page-opened-payment/form/place-order-button/google-button';
import { translate } from '@tectonics/translate';

export function PlaceOrderButton(): Node {
  const [acceptTerms] = useUserInputValue('acceptTerms');
  const buttonRef = useRef <?HTMLButtonElement> (null);
  const [googlePayment] = useUserInputValue('googlePayOutgoingPayment');
  const currentAllFeatures = useCurrentAllFeatures();
  const waitingAcceptTerms = acceptTermsValidation(acceptTerms, currentAllFeatures);

  const submitting = useSubmitting();

  const themeId = useThemeId();

  const [paymentMethod] = useUserInputValue('selectedPaymentMethod');

  useEffect(() => {
    if (googlePayment) {
      if (buttonRef.current) {
        buttonRef.current.click();
      }
    }
  }, [googlePayment]);

  const hasSelectedPaymentMethod = paymentMethod !== '';
  const isGooglePayment = paymentMethod === 'google';

  const shouldRenderEbanxPaymentButtonAsHidden = isGooglePayment && !googlePayment;

  return (
    <>
      {!submitting && shouldRenderEbanxPaymentButtonAsHidden && <GoogleButton />}
      {<Root hidden={shouldRenderEbanxPaymentButtonAsHidden} ref={buttonRef} disabled={!waitingAcceptTerms || submitting || !hasSelectedPaymentMethod} themeId={themeId} acceptTerms={waitingAcceptTerms} size="lg" type="primary" data-test-id="submit" data-testid="place-order-button">{submitting && <SpinnerImage src={spinnerImageSrc} alt="loading indicator" />}
        <ButtonContent submitting={submitting} />
      </Root>}
    </>
  );
}

const Root = styled(Button)`
  position: relative;
  font-weight: default;

  ${props => props.themeId === 'default' && css`
    text-transform: uppercase;
  `}

  ${props => props.hidden !== false && css`
    display: none;
  `}

  ${props => !props.acceptTerms && css`
    background-color:#DFE3E7;
    border: 1px solid #DFE3E7;
  `};
`;

const SpinnerImage = styled.img`
  height: 100%;
  position: absolute;
  transform: translate(-50%, -25%);
`;

function ButtonContent(props): Node {
  return (
    <ButtonContentWrapper {...props}>
      <span>
        <PadlockIcon />
        <ButtonMessage />
      </span>
    </ButtonContentWrapper>
  );
}

function ButtonMessage(): Node {
  const incomingPayment = useIncomingPayment();
  const country = getCountry(incomingPayment);

  switch (country) {
    case 'br':
      return <BrazilianButtonMessage />;
    case 'ar':
      return <ArgentineButtonMessage />;
    case 'bo':
      return <BolivianButtonMessage />;
    case 'cl':
      return <ChileanButtonMessage />;
    case 'co':
      return <ColombianButtonMessage />;
    case 'ec':
      return <EcuadorianButtonMessage />;
    case 'mx':
      return <MexicanButtonMessage />;
    case 'pe':
      return <PeruvianButtonMessage />;
    case 'uy':
      return <UruguayanButtonMessage />;
    case 'cr':
      return <CostaRicanButtonMessage />;
    case 'py':
      return <ParaguayanButtonMessage />;
    case 'pa':
      return <PanamanianButtonMessage />;
    case 'sv':
      return <ElSalvadorButtonMessage />;
    case 'gt':
      return <GuatemalanButtonMessage />;
    case 'do':
      return <DominicanButtonMessage />;
    case 'za':
      return <SouthAfricaButtonMessage />;
    case 'ke':
      return <KenyanButtonMessage />;
    case 'ng':
      return <NigerianButtonMessage />;
    case 'in':
      return <IndiaButtonMessage />;
    case 'tz':
      return <TanzanianButtonMessage />;
    case 'ug':
      return <UgandaButtonMessage />;
    case 'zm':
      return <ZambiaButtonMessage />;
    case 'eg':
      return <EgyptButtonMessage />;
    case 'gh':
      return <GhanaButtonMessage />;
    case 'jm':
      return <JamaicaButtonMessage />;

    default:
      throw new Error(`PlaceOrderButton message for country: ${country} not found`);
  }
}

function BrazilianButtonMessage(): Node {
  const [paymentMethod] = useUserInputValue('selectedPaymentMethod');

  switch (paymentMethod) {
    case 'creditcard':
      return 'Pagar com cartão de crédito';

    case 'boleto':
      return 'Gerar boleto';

    case 'banktransfer':
      return 'Gerar transferência';

    case 'tef':
      return 'Pagar com Internet Banking';

    case 'google':
      return 'Pagar com Google';

    case 'debitcard':
      return 'Pagar com cartão de débito';

    case 'ewallet':
      return 'Pagar com Carteiras Digitais';

    case 'pix':
      return 'Pagar com Pix';

    case 'vouchercard':
      return 'Pagar com Vale Refeição';

    case 'multibeneficios':
      return 'Pagar com Multibenefícios';

    case 'nupay':
      return 'Pagar com NuPay';

    case 'mpcredito':
      return 'Pagar com Mercado Crédito';

    case '':
      return 'Selecione um método de pagamento';

    default:
      throw new Error(`PlaceOrderButton message for method: ${paymentMethod} not found`);
  }
}

function MexicanButtonMessage(): Node {
  const [paymentMethod] = useUserInputValue('selectedPaymentMethod');

  switch (paymentMethod) {
    case 'creditcard':
    case 'debitcard':
      return 'Pagar con Tarjeta';

    case 'oxxo':
    case 'oxxopay':
      return 'Generar Boleta de Pago';

    case 'spei':
      return 'Pagar con Spei';

    case 'cash':
      return 'Pagar con Paynet';

    case 'ewallet':
      return 'Pagar con MercadoPago';

    case 'google':
      return 'Pagar con Google';

    case 'externalpayprovider':
      return 'Pagar con Aplazo';

    case '':
      return 'Seleccione un método de pago';

    default:
      throw new Error(`PlaceOrderButton message for method: ${paymentMethod} not found`);
  }
}

function ArgentineButtonMessage(): Node {
  const [paymentMethod] = useUserInputValue('selectedPaymentMethod');

  switch (paymentMethod) {
    case 'creditcard':
      return 'Pagar con Tarjeta de crédito';
    case 'debitcard':
      return 'Pagar con Tarjeta de débito';
    case 'cash':
      return 'Pagar con efectivo';
    case 'ewallet':
      return 'Pagar con MercadoPago';
    case '':
      return 'Seleccione un método de pago';
    case 'banktransfer':
      return 'Enviar solicitud de Debin';
    default:
      throw new Error(`PlaceOrderButton message for method: ${paymentMethod} not found`);
  }
}

function UruguayanButtonMessage(): Node {
  const [paymentMethod] = useUserInputValue('selectedPaymentMethod');

  switch (paymentMethod) {
    case 'creditcard':
      return 'Pagar con Tarjeta de crédito';
    case 'debitcard':
      return 'Pagar con Tarjeta de débito';
    case '':
      return 'Seleccione un método de pago';
    default:
      throw new Error(`PlaceOrderButton message for method: ${paymentMethod} not found`);
  }
}

function ColombianButtonMessage(): Node {
  const [paymentMethod] = useUserInputValue('selectedPaymentMethod');
  const [externalPayProvider] = useUserInputValue('selectedExternalPayProvider');

  switch (paymentMethod) {
    case 'eft':
      return 'Pagar con PAGOS EN LÍNEA';
    case 'creditcard':
      return 'Pagar con Tarjeta de crédito';
    case 'debitcard':
      return 'Pagar con Tarjeta de débito';
    case 'cash':
      return 'Pagar con Efecty';
    case 'ewallet':
      return 'Pagar con NEQUI';
    case 'externalpayprovider':
      return ColombianExternalPayProviderValidation(externalPayProvider);
    case '':
      return 'Seleccione un método de pago';
    default:
      throw new Error(`PlaceOrderButton message for method: ${paymentMethod} not found`);
  }
}

function ChileanButtonMessage(): Node {
  const [paymentMethod] = useUserInputValue('selectedPaymentMethod');

  switch (paymentMethod) {
    case 'creditcard':
      return 'Pagar con Tarjeta';
    case 'multicaja':
      return 'Pagar con Klap (Multicaja)';
    case 'webpay':
      return 'Pagar con Webpay';
    case 'sencillito':
      return 'Pagar con Sencillito';
    case 'servipag':
      return 'Pagar con ServiPag';
    case 'ewallet':
      return 'Paga con Billetera electrónica';
    case 'google':
      return 'Pagar con Google';
    case 'banktransfer':
      return 'PAGAR CON TRANSFERENCIA BANCARIA';
    case '':
      return 'Seleccione un método de pago';
    default:
      throw new Error(`PlaceOrderButton message for method: ${paymentMethod} not found`);
  }
}

function PeruvianButtonMessage(): Node {
  const [paymentMethod] = useUserInputValue('selectedPaymentMethod');

  switch (paymentMethod) {
    case 'pagoefectivo':
      return 'Pagar con PagoEfectivo';
    case 'safetypay-cash':
    case 'safetypay-online':
      return 'Pagar con Safetypay';
    case '':
      return 'Seleccione un método de pago';
    case 'creditcard':
      return 'Pagar con Tarjeta';
    case 'banktransfer':
      return 'PAGAR CON TRANSFERENCIA BANCARIA';
    default:
      throw new Error(`PlaceOrderButton message for method: ${paymentMethod} not found`);
  }
}

function BolivianButtonMessage(): Node {
  const [paymentMethod] = useUserInputValue('selectedPaymentMethod');

  switch (paymentMethod) {
    case 'cash':
      return 'Pagar con Efectivo';
    case '':
      return 'Seleccione un método de pago';
    default:
      throw new Error(`PlaceOrderButton message for method: ${paymentMethod} not found`);
  }
}

function EcuadorianButtonMessage(): Node {
  const [paymentMethod] = useUserInputValue('selectedPaymentMethod');

  switch (paymentMethod) {
    case 'safetypay-cash':
      return 'Generar codigo de pago en efectivo';
    case 'safetypay-online':
      return 'Pagar vía transferencia';
    case 'creditcard':
      return 'Pagar con Tarjeta de Crédito';
    case 'debitcard':
      return 'Pagar con Tarjeta de Débito';
    case '':
      return 'Seleccione un método de pago';
    default:
      throw new Error(`PlaceOrderButton message for method: ${paymentMethod} not found`);
  }
}

function ParaguayanButtonMessage(): Node {
  const [paymentMethod] = useUserInputValue('selectedPaymentMethod');

  switch (paymentMethod) {
    case 'creditcard':
      return 'Pagar con Tarjeta de crédito';
    case 'debitcard':
      return 'Pagar con Tarjeta de débito';
    case '':
      return 'Seleccione un método de pago';
    default:
      throw new Error(`PlaceOrderButton message for method: ${paymentMethod} not found`);
  }
}

function CostaRicanButtonMessage(): Node {
  const [paymentMethod] = useUserInputValue('selectedPaymentMethod');

  switch (paymentMethod) {
    case 'creditcard':
      return 'Pagar con Tarjeta de crédito';
    case 'debitcard':
      return 'Pagar con Tarjeta de débito';
    case 'cash':
      return 'Generar Boleta de Pago';
    case '':
      return 'Seleccione un método de pago';
    default:
      throw new Error(`PlaceOrderButton message for method: ${paymentMethod} not found`);
  }
}

function ElSalvadorButtonMessage(): Node {
  const [paymentMethod] = useUserInputValue('selectedPaymentMethod');

  switch (paymentMethod) {
    case 'creditcard':
      return 'Pagar con Tarjeta de crédito';
    case 'debitcard':
      return 'Pagar con Tarjeta de débito';
    case 'cash':
      return 'Generar Boleta de Pago';
    case '':
      return 'Seleccione un método de pago';
    default:
      throw new Error(`PlaceOrderButton message for method: ${paymentMethod} not found`);
  }
}

function GuatemalanButtonMessage(): Node {
  const [paymentMethod] = useUserInputValue('selectedPaymentMethod');

  switch (paymentMethod) {
    case 'creditcard':
      return 'Pagar con Tarjeta de crédito';
    case 'debitcard':
      return 'Pagar con Tarjeta de débito';
    case 'cash':
      return 'Generar Boleta de Pago';
    case '':
      return 'Seleccione un método de pago';
    default:
      throw new Error(`PlaceOrderButton message for method: ${paymentMethod} not found`);
  }
}

function DominicanButtonMessage(): Node {
  const [paymentMethod] = useUserInputValue('selectedPaymentMethod');

  switch (paymentMethod) {
    case 'creditcard':
      return 'Pagar con Tarjeta de crédito';
    case 'debitcard':
      return 'Pagar con Tarjeta de débito';
    case '':
      return 'Seleccione un método de pago';
    default:
      throw new Error(`PlaceOrderButton message for method: ${paymentMethod} not found`);
  }
}

function PanamanianButtonMessage(): Node {
  const [paymentMethod] = useUserInputValue('selectedPaymentMethod');

  switch (paymentMethod) {
    case 'creditcard':
      return 'Pagar con Tarjeta de crédito';
    case 'debitcard':
      return 'Pagar con Tarjeta de débito';
    case 'cash':
      return 'Generar Boleta de Pago';
    case '':
      return 'Seleccione un método de pago';
    default:
      throw new Error(`PlaceOrderButton message for method: ${paymentMethod} not found`);
  }
}

function SouthAfricaButtonMessage(): Node {
  const [paymentMethod] = useUserInputValue('selectedPaymentMethod');

  switch (paymentMethod) {
    case 'creditcard':
      return 'Pay with credit card';
    case 'debitcard':
      return 'Pay with debit card';
    case 'externalpayprovider':
      return <SouthAfricaOzowValidation />;

    case '':
      return 'Select a payment method';
    default:
      throw new Error(`PlaceOrderButton message for method: ${paymentMethod} not found`);
  }
}

function KenyanButtonMessage(): Node {
  const [paymentMethod] = useUserInputValue('selectedPaymentMethod');
  const [ewallet] = useUserInputValue('selectedEWallet');
  var ewalletName = '';

  switch (ewallet) {
    case 'mpesa':
      ewalletName = 'M-Pesa';
      break;
    case 'airtel':
      ewalletName = 'Airtel';
      break;
    case 'equitel':
      ewalletName = 'Equitel';
      break;
    case 'vodacom':
      ewalletName = 'Vodacom M-PESA';
      break;
    case 'tigo':
      ewalletName = 'Tigo Pesa';
      break;
    default:
      throw new Error(`PlaceOrderButton message for method: ${ewallet} not found`);
  }

  switch (paymentMethod) {
    case 'ewallet':
      return `Pay with ${ewalletName}`;
    case 'creditcard':
      return 'Pay with credit card';
    case 'debitcard':
      return 'Pay with debit card';
    case '':
      return 'Select a payment method';
    default:
      throw new Error(`PlaceOrderButton message for method: ${paymentMethod} not found`);
  }
}

function TanzanianButtonMessage(): Node {
  const [paymentMethod] = useUserInputValue('selectedPaymentMethod');
  const [ewallet] = useUserInputValue('selectedEWallet');
  var ewalletName = '';

  switch (ewallet) {
    case 'vodacom':
      ewalletName = 'Vodacom M-PESA';
      break;
    case 'airtel':
      ewalletName = 'Airtel';
      break;
    case 'tigo':
      ewalletName = 'Tigo Pesa';
      break;
  }

  switch (paymentMethod) {
    case 'ewallet':
      return `Pay with ${ewalletName}`;
    case '':
      return 'Select a payment method';
    default:
      throw new Error(`PlaceOrderButton message for method: ${paymentMethod} not found`);
  }
}

function NigerianButtonMessage(): Node {
  const [paymentMethod] = useUserInputValue('selectedPaymentMethod');

  switch (paymentMethod) {
    case 'banktransfer':
    case 'nibbs':
      return 'Pay with Bank Transfer';
    case 'ewallet':
      return 'Pay with USSD';
    case 'creditcard':
      return 'Pay with credit card';
    case 'debitcard':
      return 'Pay with debit card';
    case 'externalpayprovider':
      return 'Pay with OPay';
    case '':
      return 'Select a payment method';
    default:
      throw new Error(`PlaceOrderButton message for method: ${paymentMethod} not found`);
  }
}

function IndiaButtonMessage(): Node {
  const [paymentMethod] = useUserInputValue('selectedPaymentMethod');

  switch (paymentMethod) {
    case 'ewallet':
      return 'Pay with UPI';
    case 'creditcard':
      return 'Pay with credit card';
    case 'debitcard':
      return 'Pay with debit card';
    case '':
      return 'Select a payment method';
    default:
      throw new Error(`PlaceOrderButton message for method: ${paymentMethod} not found`);
  }
}

function SouthAfricaOzowValidation(): Node {
  const incomingPayment = useIncomingPayment();
  const paymentTypeCode = getSafePaymentTypeCode(incomingPayment);

  switch (paymentTypeCode) {
    case 'ozow':
      return 'Pay with Ozow';
    default:
      return 'Proceed with payment';
  }
}
function UgandaButtonMessage(): Node {
  const [paymentMethod] = useUserInputValue('selectedPaymentMethod');
  const [ewallet] = useUserInputValue('selectedEWallet');
  var ewalletName = '';

  switch (ewallet) {
    case 'mtn':
      ewalletName = 'MTN MoMo';
      break;
    case 'airtel':
      ewalletName = 'Airtel';
      break;
  }

  switch (paymentMethod) {
    case 'ewallet':
      return `Pay with ${ewalletName}`;
    case '':
      return 'Select a payment method';
    default:
      throw new Error(`PlaceOrderButton message for method: ${paymentMethod} not found`);
  }
}

function ZambiaButtonMessage(): Node {
  const [paymentMethod] = useUserInputValue('selectedPaymentMethod');
  const [ewallet] = useUserInputValue('selectedEWallet');
  var ewalletName = '';

  switch (ewallet) {
    case 'mtn':
      ewalletName = 'MTN MoMo';
      break;
    case 'airtel':
      ewalletName = 'Airtel';
      break;
    case 'zamtel':
      ewalletName = 'Zamtel';
      break;
  }

  switch (paymentMethod) {
    case 'ewallet':
      return `Pay with ${ewalletName}`;
    case '':
      return 'Select a payment method';
    default:
      throw new Error(`PlaceOrderButton message for method: ${paymentMethod} not found`);
  }
}

function GhanaButtonMessage(): Node {
  const [paymentMethod] = useUserInputValue('selectedPaymentMethod');
  const [ewallet] = useUserInputValue('selectedEWallet');
  var ewalletName = '';

  switch (ewallet) {
    case 'airtel':
      ewalletName = 'Airtel';
      break;
  }

  switch (paymentMethod) {
    case 'ewallet':
      return `Pay with ${ewalletName}`;
    case '':
      return 'Select a payment method';
    default:
      throw new Error(`PlaceOrderButton message for method: ${paymentMethod} not found`);
  }
}

function EgyptButtonMessage(): Node {
  const [paymentMethod] = useUserInputValue('selectedPaymentMethod');

  switch (paymentMethod) {
    case 'creditcard':
      return translate('volcanes.payment-methods.card.creditcardButtonMessage');
    case 'debitcard':
      return translate('volcanes.payment-methods.card.debitcardButtonMessage');
    case 'externalpayprovider':
      return translate('volcanes.payment-methods.externalpayprovider.fawryLabel');
    case '':
      return translate('volcanes.selectPaymentMethod');
    default:
      throw new Error(`PlaceOrderButton message for method: ${paymentMethod} not found`);
  }
}

function JamaicaButtonMessage(): Node {
  const [paymentMethod] = useUserInputValue('selectedPaymentMethod');

  switch (paymentMethod) {
    case 'creditcard':
      return 'Pay with credit card';
    case 'debitcard':
      return 'Pay with debit card';
    case '':
      return 'Select a payment method';
    default:
      throw new Error(`PlaceOrderButton message for method: ${paymentMethod} not found`);
  }
}


function ColombianExternalPayProviderValidation(externalpayprovider: string): Node {

  switch (externalpayprovider) {
    case 'achpse':
      return 'Paga con PSE AVANZA';
    case 'bancolombia-button':
      return 'Paga con BANCOLOMBIA';
    default:
      return 'Seleccione un método de pago';
  }
}

const ButtonContentWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  ${props => props.submitting && css`
    visibility: hidden;
  `}
`;

const PadlockIcon = styled.i`
  &:before {
    content: "";
    padding: .375em;
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
    background-image: url(${padlockImageSrc});

    [dir=ltr] & {
      margin-right: .8em;
    }

    [dir=rtl] & {
      margin-left: .8em;
    }
  }
`;

function acceptTermsValidation(acceptTerms: bool, currentAllFeatures: ImmutableArray<Feature>): bool {
  const shouldValidateAcceptTermsArgentine = hasFeature(currentAllFeatures, 'field_argentine_accept_terms_withhold_taxes');
  const shouldValidateAcceptTermsKenyan = hasFeature(currentAllFeatures, 'field_kenyan_accept_terms_for_international_transfer');

  if (!(shouldValidateAcceptTermsKenyan || shouldValidateAcceptTermsArgentine)) {
    return true;
  }

  return acceptTerms;
}
